import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ReplyIcon from "@mui/icons-material/Reply";
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MaterialAvatar from "../../components/general/MaterialAvatar";
import { baseAtom, selectInterviewWithInsight } from "../../stores/jotai/base";
import { GetFindingTableResponse } from "../../tanstackQuery/queries/findings/type";
import { getQuote, getQuoteDescription } from "../../util/insights/handle-export";
import FilterInsights from "./filter-insights";
import { InsightDetailsSkeleton } from "./insight-skeleton";
import { InsightAudio } from "./insight-table";
import ThumbsDownIcon from "../../../icons/ThumbsDownIcon";
import ThumbsUpIcon from "../../../icons/ThumbsUpIcon";
import { renderRatingColumn } from ".";
import CollapsibleTypography from "../generics/components/collapsible-typography";
import { useGetMetaArtifacts } from "../../tanstackQuery/queries/artifacts";
import { useKeycloak } from "@react-keycloak/web";
import AnalysisInsightCards from "../discovery/analysis/analysis-insights-details";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";

interface InsightDetailsProps {
  insight: GetFindingTableResponse;
  filters: {
    allTags: boolean;
    change: boolean;
    unTaggedInsights: boolean;
  };
  setFilters: Dispatch<SetStateAction<InsightDetailsProps["filters"]>>;
  matchedInsightGroup?:any;
  setMatchedInsightGroup?:Function;
}

export const extractInsightGroup = (findingID: string, metaArtifactData: any) => {
  if (!metaArtifactData) return undefined;

  const { explicit = {}, implicit = {}, meta = {} } = metaArtifactData;

  const match =
    explicit?.[findingID] ?? implicit?.[findingID] ?? meta?.[findingID];

  return match || undefined;
};


export function InsightDetails({
  insight: selectInsight,
  filters,
  setFilters,
  matchedInsightGroup,
  setMatchedInsightGroup
}: InsightDetailsProps) {
  const theme = useTheme();
  const [base, setBase] = useAtom(baseAtom);
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  
  const [copyType, setCopyType] = useState<"quote" | "comment" | null>(null);

  const client = useQueryClient();
  const interviews = client.getQueryData([
    "interviews",
    { groupID: base.team.groupID }
  ]) as any[];

  let quoteValue;
  try {
    quoteValue = JSON.parse(selectInsight?.quote);
  } catch (e) {
    quoteValue = null;
  }

  const {
    refetch: refetchMetaArtifacts,
    isFetching,
    data: metaArtifactData,
    isFetched,
  } = useGetMetaArtifacts(
    { accessToken: keycloak.token },
    { artifactID: selectInsight?.artifactID, isMappedByParent: true },
    {
      enabled: false, 
      // retry: 1,
      // retryDelay: 3000,
    }
  );

  const gotoInsight = (finding) => {
    if (finding) {
      const transformedFinding = { ...finding };

      if(quoteValue?.source !== "note" && quoteValue?.source !== "transcript"){
        transformedFinding.childFindingIDs= transformedFinding.childFindings.map(f => f.findingID)
        delete transformedFinding.childFindings;
      }

      selectInterviewWithInsight(
        setBase,
        finding?.interviewID,
        transformedFinding,
        interviews
      );

      navigate(
        `/discovery/${finding?.interviewID}/${finding?.artifactID}/${finding?.findingID}`
      );
    }
  };

  const members = base.members;
  if (members === undefined) return <LinearProgress />;

  return (
    <Box
      mt={1}
      style={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Box
        mb={0.6}
        onClick={(e) => e?.stopPropagation()}
        style={{
          backgroundColor: theme.custom.mainBackgroundColor,
        }}
      >
        <FilterInsights filters={filters} setFilters={setFilters} />
      </Box>
      <Box
        className="top-level-discovery"
        onClick={(e) => {
          e?.stopPropagation();
        }}
        style={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "white",
          padding: "10px",
          border: `1px solid ${theme?.custom?.borderGrey}`,
          borderRadius: "2px",
          height: "calc(100vh - 129px)",
          overflowY: "auto",
        }}
      >
        {selectInsight ? (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              paddingBottom: "10px",
            }}
          >
            {
                isFetching && <LinearProgress />
            }
            <Box
              sx={{
                display: "flex",
                borderBottom: `1px solid ${theme?.custom?.borderGrey}`,
                paddingBottom: "10px",
                justifyContent: "space-between",
                width:"100%"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title={selectInsight?.avatar?.name}>
                  <>
                    <MaterialAvatar
                      sx={{
                        height: 40,
                        width: 40,
                      }}
                      user={{
                        name: selectInsight?.avatar?.name,
                        photoURL: selectInsight?.avatar?.src,
                      }}
                    />
                  </>
                </Tooltip>
                <Box style={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      paddingX: "10px",
                      wordBreak: "break-all",
                    }}
                  >
                    {selectInsight?.interviewee}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: "400",
                      fontSize: "10px",
                      paddingX: "10px",
                    }}
                  >
                    {selectInsight?.created.split(" ").slice(0, 4).join(" ")}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection:"column",
                  justifyContent: "space-between",
                  gap:"5px"
                }}
              >
                <Tooltip title="Go to Insight">
                  <IconButton
                    className="clickSafe"
                    onClick={() => gotoInsight(selectInsight)}
                    sx={{ padding: "0px", alignSelf: "flex-end" }}
                  >
                    <OpenInNewRoundedIcon />
                  </IconButton>
                </Tooltip>
                {selectInsight?.rating?.global?.rating !== 0 && (
                  <div>
                    {renderRatingColumn(
                      selectInsight?.rating?.global?.rating || 0
                    )}
                  </div>
                )}
              </Box>
            </Box>

            <Stack spacing={1}>
              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: "bold",
                    padding: "5px 0px",
                    fontSize: "15px",
                  }}
                >
                  Quote:
                </Typography>
                <Box style={{ display: "flex", alignItems: "center" }}>
                  <InsightAudio
                    finding={selectInsight}
                    isDetails
                    selected={false}
                  />
                  <Tooltip
                    title={copyType === "quote" ? "" : `Copy to Clipboard`}
                  >
                    <>
                      <IconButton
                        size="small"
                        onClick={() => {
                          navigator?.clipboard.writeText(
                            getQuote(selectInsight?.quote)
                          );
                          setCopyType("quote");
                          setTimeout(() => setCopyType(null), 500);
                        }}
                      >
                        {copyType === "quote" ? (
                          <CheckIcon sx={{ fontSize: "18px" }} />
                        ) : (
                          <ContentCopyIcon sx={{ fontSize: "18px" }} />
                        )}
                      </IconButton>
                    </>
                  </Tooltip>
                </Box>
              </Stack>
              <Typography
                sx={{
                  fontStyle: "italic",
                  overflowWrap: "break-word",
                  fontSize: "15px",
                }}
              >
                {quoteValue?.source === "note" || quoteValue?.source === "transcript" ? (
                  getQuote(selectInsight?.quote)
                ) : <AnalysisInsightCards
                      key={selectInsight?.findingID}
                      item={
                        matchedInsightGroup ?? {
                          title: getQuote(selectInsight?.quote),
                          description: getQuoteDescription(selectInsight?.quote),
                          insights: [],
                        }
                      }
                      showInsights={false}
                      highlightCard={false}
                      collapsible={true}
                      onExpand={async () => {
                        if (!matchedInsightGroup) {
                          const result = await refetchMetaArtifacts();
                          const newMeta = result?.data;

                          if (newMeta) {
                            const found = extractInsightGroup(selectInsight.findingID, newMeta);
                            if (found) {
                              setMatchedInsightGroup(found); 
                            }
                          }
                        }
                      }}
                      isSelectable={true}
                      headingStyle={{
                        fontSize:"15px"
                      }}
                  />
                }
              </Typography>
            </Stack>
            <Box>
              <Box style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontWeight: "bold", padding: "5px 0px" }}>
                  Comment:
                </Typography>
                <Tooltip
                  title={copyType === "comment" ? "" : `Copy to Clipboard`}
                >
                  <>
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(selectInsight?.comment);
                        setCopyType("comment");
                        setTimeout(() => setCopyType(null), 500);
                      }}
                    >
                      {copyType === "comment" ? (
                        <CheckIcon sx={{ fontSize: "18px" }} />
                      ) : (
                        <ContentCopyIcon sx={{ fontSize: "18px" }} />
                      )}
                    </IconButton>
                  </>
                </Tooltip>
              </Box>
              <Typography
                sx={{
                  fontStyle: "italic",
                  overflowWrap: "break-word",
                  fontSize: "15px",
                }}
              >
                {selectInsight?.comment}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
                padding: "5px 0px",
              }}
            >
              <Typography sx={{ fontWeight: "bold" }}>Tags:</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {selectInsight?.tags?.map((tag) => {
                  return (
                    tag.label && (
                      <Box
                        key={tag.customFindingTypeID as string}
                        style={{
                          border: `1px solid ${theme?.custom?.borderGrey}`,
                          borderRadius: "4px",
                          width: "fit-content",
                          backgroundColor:
                            tag?.color || theme?.custom?.disabledColor,
                          padding: "2px 6px",
                          minHeight: "28px",
                          height: "auto",
                        }}
                      >
                        <Typography sx={{ fontSize: "15px" }}>
                          {tag?.label}
                        </Typography>
                      </Box>
                    )
                  );
                })}
              </Box>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: "bold",
                  padding: "5px 0px",
                  fontSize: "15px",
                }}
              >
                Links:
              </Typography>
              <Stack spacing={3}>
                {selectInsight?.links?.map((link) => {
                  const entryData = link?.category?.entry?.entry;
                  const hypothesis = link?.category?.name;

                  let lead = {
                    label: "Supports",
                    icon: ThumbsUpIcon,
                    color: theme.custom.validInsightGreen,
                  };
                  if (link.supports === 0) {
                    lead = {
                      label: "Does Not Support",
                      icon: ThumbsDownIcon,
                      color: theme.custom.invalidInsightRed,
                    };
                  }

                  const Icon = lead.icon;

                  return (
                    entryData &&
                    hypothesis && (
                      <Box>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          <Box
                            sx={{
                              border: `1px solid ${theme.custom.mainButtonHighlight}`,
                              color: theme?.custom.mainButtonHighlight,
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontSize: "15px",
                              height: "28px",
                            }}
                          >
                            {hypothesis}
                          </Box>

                          <Box
                            style={{
                              display: "flex",
                              alignItems: "center",
                              alignContent: "center",
                              border: `1px solid ${theme.custom.borderGrey}`,
                              color: theme.custom.mainHeaderColor,
                              padding: "2px 6px",
                              borderRadius: "4px",
                              gap: 1,
                              height: "28px",
                            }}
                          >
                            {/* @ts-ignore */}
                            <Icon sx={{ color: lead.color, marginTop: 0.4 }} />
                            <Typography sx={{ fontSize: "15px" }}>
                              {lead.label}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography sx={{ fontSize: "15px" }} mt={1}>
                          {entryData}
                        </Typography>
                      </Box>
                    )
                  );
                })}
              </Stack>
            </Box>

          </Box>
        ) : (
          <InsightDetailsSkeleton />
        )}
      </Box>
    </Box>
  );
}
